import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import Accordion from 'react-bootstrap/Accordion'
import { Link, useParams } from 'react-router-dom'
import KLayerNavigation from '../components/KLayerNavigation'
import CheckHit from '../components/CheckHit'
import AmlChecksPanel from '../components/AmlChecksPanel'
import { isUserLoggedIn } from '../helpers'
import { downloadSurveyPdf } from '../helpers/surveyPdf'
import { t } from '../locale'
import { Client, KycCheck } from '../react-app-env'
import {
  fetchClientById,
  fetchIdentityParams,
  fetchIdentityPdf,
  fetchIdentityVideo,
  fetchKycChecks,
  updateKycStatus,
} from '../service/client'
import { getServiceKeys } from '../helpers/services.helper'

function View() {
  const { id: routeClientId } = useParams<{ id?: string }>()
  const pathSegments = window.location.pathname.split('/')
  const clientId = routeClientId || pathSegments[3] || localStorage.getItem('clientId') || ''

  const [identityCompanyId, setIdentityCompanyId] = useState('')
  const [identityRequestId, setIdentityRequestId] = useState('')
  const [identityExist, setIdentityExist] = useState(false)

  const amlPdfRef = useRef<HTMLDivElement>(null)

  const [state, setState] = useState({
    client: {} as Client,
    kycCheck: [] as any[],
    currentKyc: {} as KycCheck,
  })

  const updateState = useCallback(
    (newState: Partial<typeof state>) => setState(prevState => ({ ...prevState, ...newState })),
    []
  )

  const loadClient = useCallback(async () => {
    const result = await await fetchClientById(clientId)
    if (result) updateState({ client: result.client })
  }, [clientId, updateState])

  const kycChecks = useCallback(async () => {
    try {
      const response = await fetchKycChecks(clientId)
      response.ok && updateState({ kycCheck: response.result || [] })
    } catch (error) {
      console.error('Error fetching KYC checks:', error)
    }
  }, [clientId, updateState])

  const identityParams = useCallback(async () => {
    const response = await fetchIdentityParams(localStorage.getItem('clientId'))

    if (response.ok) {
      setIdentityExist(true)
      setIdentityRequestId(response.identity.identityRequestId)
      setIdentityCompanyId(response.identity.identityCompanyId)
    }
  }, [])

  useEffect(() => {
    if (!clientId) {
      return
    }

    loadClient()
    kycChecks()
    identityParams()
  }, [clientId, loadClient, kycChecks, identityParams])

  const kycCheckDelete = async (kycNummer: number, kycStatus: string) => {
    if (!clientId) {
      alert(t('clientIdIsMissing'))
      return
    }

    const response = await updateKycStatus(clientId, kycNummer, kycStatus)
    response && kycChecks()
  }

  const showPdf = async () => {
    try {
      const response = await fetchIdentityPdf(identityCompanyId, identityRequestId)

      if (!response.ok) {
        throw new Error('PDF nije pronađen')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (error) {
      console.error(error)
      alert('Greška prilikom otvaranja PDF-a.')
    }
  }

  const handleGenerateAmlPdf = () => {
    const savedSection = amlPdfRef.current?.querySelector<HTMLElement>(
      '[data-pdf-section="aml-saved"]'
    )
    if (!savedSection) return
    downloadSurveyPdf(savedSection, {
      filename: `AML_Checks_${state.client.clientNumber ?? ''}`.replace(/\s+/g, '_'),
      heading: t('amlChecks'),
      subheading: `${t('clientNumber')}: ${state.client.clientNumber ?? '—'}${state.client.name ? '  ·  ' + state.client.name : ''}`,
    }).catch(err => console.error('AML PDF generation failed:', err))
  }

  const showVideo = async () => {
    try {
      const response = await fetchIdentityVideo(identityCompanyId, identityRequestId)

      console.log(response)
      if (!response.ok) {
        throw new Error('PDF nije pronađen')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (error) {
      console.error(error)
      alert('Greška prilikom otvaranja videa.')
    }
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>

        <main className='col-md-9 ms-sm-auto col-lg-10 px-md-4 mb-5'>
          <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary d-block'>
              {t('previewClient')}
            </p>
          </div>

          <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-1 pb-2 mb-3 border-bottom'></div>

          <Row className='g-2'>
            <Col xs={12} md={8}>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={2} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('clientNumber')}</p>
                </Col>
                <Col xs={2} md={3}>
                  <p className='fw-medium mb-0'>{state.client.clientNumber || '—'}</p>
                </Col>
              </Row>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('email')}</p>
                </Col>
                <Col xs={12} md={2}>
                  <p className='fw-medium mb-0'>{state.client.email || '—'}</p>
                </Col>
              </Row>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('isPerson')}</p>
                </Col>
                <Col xs={12} md={2}>
                  <p className='fw-medium mb-0'>{state.client.isPerson ? t('yes') : t('no')}</p>
                </Col>
              </Row>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('gender')}</p>
                </Col>
                <Col xs={12} md={2}>
                  <p className='fw-medium mb-0'>{t(state.client.gender) || '—'}</p>
                </Col>
              </Row>

              <Row className='g-2 mt-4'>
                <p className='text-primary mb-2'>{t('personalData')}</p>
              </Row>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('firstName')}</p>
                </Col>
                <Col className='g-2'>
                  <p className='fw-medium mb-0'>{state.client.firstName || '—'}</p>
                </Col>
              </Row>
              <Row className='pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('lastName')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.name || '—'}</p>
                </Col>
              </Row>
              <Row className='pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('dateOfBirth')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.dateOfBirth || '—'}</p>
                </Col>
              </Row>
              <Row className='pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('country')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.country || '—'}</p>
                </Col>
              </Row>
              <Row className='g-2 mt-4'>
                <p className='text-primary mb-2'>{t('address')}</p>
              </Row>
              <Row className='g-2 pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('streetAddress')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.streetAddress || '—'}</p>
                </Col>
              </Row>
              <Row className='pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('zipCode')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.zipCode || '—'}</p>
                </Col>
              </Row>
              <Row className='pb-2 mb-2 border-bottom'>
                <Col xs={12} md={3}>
                  <p className='text-uppercase text-muted small mb-0'>{t('city')}</p>
                </Col>
                <Col xs={12} md={3}>
                  <p className='fw-medium mb-0'>{state.client.city || '—'}</p>
                </Col>
              </Row>
            </Col>

            <Col xs={12} md={4} className='border-start'>
              <div className='bg-light rounded p-3'>
                <p className='text-primary mb-2'>{t('contacts')}</p>
                {state.client.contacts?.map((contact, index) => (
                  <div className='mb-3' key={index}>
                    <Row className='g-2 pb-1 mb-1 border-bottom'>
                      <Col xs={5}>
                        <p className='text-uppercase text-muted small mb-0'>{t('name')}</p>
                      </Col>
                      <Col xs={7}>
                        <p className='fw-medium mb-0'>{contact.name || '—'}</p>
                      </Col>
                    </Row>
                    <Row className='g-2 pb-1 mb-1 border-bottom'>
                      <Col xs={5}>
                        <p className='text-uppercase text-muted small mb-0'>{t('email')}</p>
                      </Col>
                      <Col xs={7}>
                        <p className='fw-medium mb-0'>{contact.email || '—'}</p>
                      </Col>
                    </Row>
                    <Row className='g-2'>
                      <Col xs={5}>
                        <p className='text-uppercase text-muted small mb-0'>{t('phone')}</p>
                      </Col>
                      <Col xs={7}>
                        <p className='fw-medium mb-0'>{contact.phone || '—'}</p>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row className='g-2'>
            <Col xs={12}>
              <hr className='my-4' />
            </Col>
          </Row>

          {getServiceKeys().includes('kycCheck') && (
            <div className='checks-section'>
              <div className='checks-section__title'>
                <i className='bi bi-shield-check' />
                {t('kycChecks')}
              </div>

              <div>
                <Accordion className='mb-4'>
                  <Accordion.Item eventKey='0'>
                    <Accordion.Header>{t('kycChecksSaved')}</Accordion.Header>
                    <Accordion.Body>
                      {state.kycCheck.some(group =>
                        group.items?.some((item: any) => item.Status === 'OK')
                      ) ? (
                        <Accordion>
                          {state.kycCheck.map((group, groupIndex) => {
                            const savedItems =
                              group.items?.filter((item: any) => item.Status === 'OK') || []
                            if (savedItems.length === 0) return null
                            return (
                              <Accordion.Item eventKey={`saved-${groupIndex}`} key={groupIndex}>
                                <Accordion.Header>
                                  {/* <small> */}
                                  {group.query} | {t('date')}: {group.createdAt}
                                  {/* </small> */}
                                </Accordion.Header>
                                <Accordion.Body>
                                  {savedItems.map((item: any, index: number) => (
                                    <CheckHit
                                      key={index}
                                      hit={item}
                                      onDelete={
                                        item.Eintrag?.Nummer != null
                                          ? () => kycCheckDelete(item.Eintrag.Nummer, 'deleted')
                                          : undefined
                                      }
                                    />
                                  ))}
                                </Accordion.Body>
                              </Accordion.Item>
                            )
                          })}
                        </Accordion>
                      ) : (
                        <p>{t('noResults')}</p>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>

                  <Accordion.Item eventKey='1'>
                    <Accordion.Header>{t('kycChecksDeleted')}</Accordion.Header>
                    <Accordion.Body>
                      {state.kycCheck.some(group =>
                        group.items?.some((item: any) => item.Status === 'deleted')
                      ) ? (
                        <Accordion>
                          {state.kycCheck.map((group, groupIndex) => {
                            const deletedItems =
                              group.items?.filter((item: any) => item.Status === 'deleted') || []
                            if (deletedItems.length === 0) return null
                            return (
                              <Accordion.Item eventKey={`deleted-${groupIndex}`} key={groupIndex}>
                                <Accordion.Header>
                                  <small>
                                    {t('query')}: {group.query} | {t('date')}: {group.createdAt}
                                  </small>
                                </Accordion.Header>
                                <Accordion.Body>
                                  {deletedItems.map((item: any, index: number) => (
                                    <CheckHit
                                      key={index}
                                      hit={item}
                                      onRestore={
                                        item.Eintrag?.Nummer != null
                                          ? () => kycCheckDelete(item.Eintrag.Nummer, 'OK')
                                          : undefined
                                      }
                                    />
                                  ))}
                                </Accordion.Body>
                              </Accordion.Item>
                            )
                          })}
                        </Accordion>
                      ) : (
                        <p>{t('noResults')}</p>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </div>
            </div>
          )}

          <div ref={amlPdfRef}>
            <AmlChecksPanel
              clientId={clientId}
              savedActions={
                <Button
                  variant='outline-primary'
                  size='sm'
                  className='no-print'
                  onClick={handleGenerateAmlPdf}
                >
                  <i className='bi bi-file-earmark-pdf me-2' />
                  {t('PDF')}
                </Button>
              }
            />
          </div>

          {identityExist && (
            <div className='card mb-3 shadow-sm border-primary'>
              <div className='card-body'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div>
                    <h6 className='mb-1'>Verification Document</h6>
                    <small className='text-muted'>ds-identity.pdf</small>
                  </div>
                  <button onClick={showPdf} className='btn btn-outline-primary btn-sm'>
                    <i className='bi bi-file-earmark-pdf me-1'></i>
                    View PDF
                  </button>
                </div>

                <hr className='my-2' />

                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <h6 className='mb-1'>Verification Video</h6>
                    <small className='text-muted'>Recorded video</small>
                  </div>
                  <button onClick={showVideo} className='btn btn-outline-secondary btn-sm'>
                    <i className='bi bi-camera-video me-1'></i>
                    View Video
                  </button>
                </div>
              </div>
            </div>
          )}

          <Row className='gx-2 mb-4'>
            <div className='col-12 mx-auto'>
              <hr className='my-4' />
            </div>
            <div className='col-6 text-start'>
              <Link
                to={`/clients?clientStatus=${true}&&deleted=empty`}
                className='btn btn-warning text-white'
              >
                « {t('cancel')}
              </Link>
            </div>
            <div className='col-6 text-end'>
              <Link to={`/client/${state.client._id}/edit`} className='btn btn-success text-white'>
                {t('edit')} »
              </Link>
            </div>
          </Row>
        </main>
      </Row>
    </Container>
  )
}

export default View
